import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import type {
  AccordionContentProps,
  AccordionTriggerProps,
  AccordionItemProps,
} from './types';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('eye:border-b eye:border-b-grey-20', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(
  (
    {
      className,
      children,
      chevronPosition = 'right',
      headerClassName,
      headerAs: HeaderTag = 'h3',
      ...props
    },
    ref,
  ) => {
    const chevron = (
      <ChevronDown
        className='eye:text-muted-foreground eye:size-20 eye:shrink-0 eye:transition-transform eye:duration-200'
        aria-hidden='true'
      />
    );

    const alignmentClasses =
      chevronPosition === 'left'
        ? 'eye:justify-start eye:gap-8'
        : 'eye:justify-between';

    return (
      <AccordionPrimitive.Header asChild>
        <HeaderTag className={cn('eye:flex', headerClassName)}>
          <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
              'eye:body-m-highlight',
              'eye:flex eye:flex-1',
              'eye:cursor-pointer eye:items-center',
              'eye:py-16 eye:text-left eye:font-medium',
              'eye:outline-offset-3 eye:outline-black',
              'eye:transition-all eye:hover:underline',
              'eye:focus-visible:outline-2 eye:focus-visible:outline-black',
              'eye:[&[data-state=open]>svg]:rotate-180',
              alignmentClasses,
              className,
            )}
            {...props}
          >
            {chevronPosition === 'left' && chevron}
            {children}
            {chevronPosition === 'right' && chevron}
          </AccordionPrimitive.Trigger>
        </HeaderTag>
      </AccordionPrimitive.Header>
    );
  },
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'eye:body-m',
      'eye:overflow-hidden eye:data-[state=closed]:animate-accordion-up eye:data-[state=open]:animate-accordion-down',
    )}
    {...props}
  >
    <div className={cn('eye:mb-16 eye:pt-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
